import React, { useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Ship, Landmark, Briefcase, Users, ArrowRight, CheckCircle } from 'lucide-react';
import Invent from '../assets/images/events.jpg';

const FEATURES = [
  {
    icon: <Ship size={22} />,
    title: 'Odyssey of the Seas',
    desc: 'Premium cruising with world-class dining, entertainment, and accommodations across the Mediterranean.'
  },
  {
    icon: <Landmark size={22} />,
    title: 'Rome Exploration',
    desc: 'Immersive cultural access to the ancient city’s landmarks, history, and curated local experiences.'
  },
  {
    icon: <Briefcase size={22} />,
    title: 'Seamless Logistics',
    desc: 'Airport transfers, port navigation, and inter-city travel fully managed and coordinated.'
  },
  {
    icon: <Users size={22} />,
    title: 'Dedicated Support',
    desc: 'Coordination and concierge services from the Oscar Global team from departure to return.'
  }
];

const DETAILS = [
  { icon: <Ship size={18} />, text: 'Vessel: Royal Caribbean Odyssey of the Seas' },
  { icon: <Landmark size={18} />, text: 'Destinations: Mediterranean & Ancient Rome' },
  { icon: <CheckCircle size={18} />, text: 'Coordination: Oscar Global Tours & Travel' },
  { icon: <Users size={18} />, text: 'Support: Dedicated concierge & logistics team' }
];

const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

.events-root {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.events-root * {
  box-sizing: border-box;
}

.events-root h1,
.events-root h2,
.events-root h3 {
  font-family: 'Sora', 'Inter', sans-serif;
  color: var(--text);
}

.events-root p {
  color: var(--text-muted);
}

.events-root :focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 3px;
  border-radius: 8px;
}

.container {
  width: min(1180px, calc(100% - 2.5rem));
  margin-inline: auto;
}

.section {
  padding-block: clamp(4rem, 8vw, 6.75rem);
}
.sectiona {
  margin-top: 4rem;
}

.hero {
  padding-top: clamp(8rem, 12vw, 10rem);
  text-align: center;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.48rem 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--primary);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.h1 {
  font-size: clamp(2.5rem, 5vw, 3.75rem);
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin: 1.25rem 0 1.5rem;
  font-weight: 700;
}

.lead {
  font-size: 1.125rem;
  line-height: 1.7;
  color: var(--text-muted);
  max-width: 640px;
  margin: 0 auto;
}

.flyer-section {
  padding-top: 3rem;
}

.flyer-wrapper {
  max-width: 860px;
  margin: 0 auto;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  background: var(--surface);
}

.flyer-wrapper img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.features-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  margin-top: 3rem;
}

.feature-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 60px rgba(15, 118, 110, 0.15);
}

.feature-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, rgba(15, 118, 110, 0.12), rgba(124, 58, 237, 0.08));
  color: var(--primary);
  margin-bottom: 1.25rem;
  border: 1px solid var(--border);
}

.feature-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
}

.feature-card p {
  margin: 0;
  line-height: 1.65;
  font-size: 0.95rem;
}

.details-card {
  max-width: 720px;
  margin: 4rem auto 0;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: 24px;
  padding: clamp(2rem, 5vw, 3.5rem);
  box-shadow: var(--shadow);
  text-align: center;
  backdrop-filter: blur(14px);
}

.details-card h2 {
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  margin: 0 0 1rem;
  font-weight: 700;
}

.details-card > p {
  max-width: 520px;
  margin: 0 auto 2rem;
  line-height: 1.7;
}

.details-list {
  display: grid;
  gap: 1rem;
  margin: 2rem 0 2.5rem;
  text-align: left;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}

.details-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;
  color: var(--text);
  font-weight: 500;
}

.details-item svg {
  color: var(--primary);
  flex-shrink: 0;
}

.btn-primary {
  background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
  color: #ffffff;
  box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28);
  min-height: 52px;
  border-radius: 999px;
  padding: 0.9rem 1.8rem;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  text-decoration: none;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36);
}

@media (min-width: 768px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .container {
    width: calc(100% - 2rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .events-root *,
  .events-root *::before,
  .events-root *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
`;

const Events = ({ isDarkMode = false }) => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const previousBackground = document.body.style.backgroundColor;
    const previousColor = document.body.style.color;
    const previousColorScheme = document.body.style.colorScheme;

    document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
    document.body.style.color = isDarkMode ? '#E9F6F3' : '#132E29';
    document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';

    return () => {
      document.body.style.backgroundColor = previousBackground;
      document.body.style.color = previousColor;
      document.body.style.colorScheme = previousColorScheme;
    };
  }, [isDarkMode]);

  const reveal = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: '0px 0px -10% 0px' },
          transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }
        };

  const heroReveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 26 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
      };

  return (
    <main
      className="events-root"
      style={{
        '--bg': isDarkMode ? '#061110' : '#F7FBFA',
        '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
        '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
        '--surface-2': isDarkMode ? 'rgba(10, 27, 25, 0.78)' : '#F4FAF9',
        '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
        '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
        '--text': isDarkMode ? '#E9F6F3' : '#132E29',
        '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
        '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
        '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
        '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
        '--accent': isDarkMode ? '#F59E0B' : '#B45309',
        '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.65)' : 'rgba(15, 118, 110, 0.45)',
        '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
      }}
    >
      <style>{globalStyles}</style>

      {/* HERO */}
      <section className="section sectiona hero" aria-labelledby="events-heading">
        <div className="container">
          <motion.div {...heroReveal}>
            <span className="eyebrow">Exclusive Itinerary · 2026</span>
            <h1 id="events-heading" className="h1">
              The Royal Caribbean &<br />Rome Odyssey
            </h1>
            <p className="lead">
              An exclusive itinerary combining luxury sea travel aboard the Odyssey of the Seas 
              with immersive cultural exploration in Rome. All logistics, transfers, and premium 
              experiences are fully coordinated by Oscar Global.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FLYER VISUAL */}
      <section className="flyer-section" aria-label="Event Flyer">
        <div className="container">
          <motion.div 
            className="flyer-wrapper"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <img 
              src={Invent} 
              alt="Royal Caribbean and Rome Event Flyer" 
              loading="eager"
            />
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section" aria-labelledby="features-heading">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <motion.h2 
              id="features-heading" 
              style={{ fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', margin: 0, fontWeight: 700 }}
              {...reveal()}
            >
              What's Included
            </motion.h2>
          </div>

          <div className="features-grid">
            {FEATURES.map((feature, index) => (
              <motion.article
                key={feature.title}
                className="feature-card"
                {...reveal(index * 0.08)}
              >
                <div className="feature-icon" aria-hidden="true">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILS & CTA */}
      <section className="section" style={{ paddingTop: 0 }} aria-labelledby="details-heading">
        <div className="container">
          <motion.div className="details-card" {...reveal()}>
            <h2 id="details-heading">Event Details</h2>
            <p>
              Secure your place on this curated journey. Our team handles the complexity 
              so you can focus on the experience.
            </p>

            <div className="details-list">
              {DETAILS.map((item, index) => (
                <div key={index} className="details-item">
                  {item.icon}
                  <span>{item.text}</span>
                </div>
              ))}
            </div>

            <button
              className="btn-primary"
              onClick={() => navigate('/booking?event=royal-caribbean-rome-2026')}
            >
              Reserve Your Spot
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Events;